import { ColumnDimension } from "@components/common/data-grid/adm-data-grid-types";
import {
  SEARCH_MEMBERS_TABLE_COLUMN_LABELS,
  SearchMembersTableColumnField,
  SearchMembersTableGridColDef,
} from "@pages/home/search/search-tables/search-members-tables/search-members-table-types";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { UtilsTypes } from "@stellar/utils";
import { getMemberDisplayName } from "@utils/data-display";
import {
  MemberCreatedAtCell,
  MemberEmailCell,
  MemberNameCell,
  MemberOptionCell,
  MemberStatusCell,
  MemberWorkspaceCell,
} from "@utils/data-grid-utils/members-data-grid-cells";
import { useMemo } from "react";

const columnDimension: ColumnDimension<SearchMembersTableColumnField> = {
  name: {
    flex: 1.5,
  },
  email: {
    flex: 1.5,
  },
  workspace: {
    flex: 2.5,
  },
  status: {
    flex: 1.5,
  },
  createdAt: {
    flex: 1,
  },
  options: {
    flex: 1,
    maxWidth: 120,
  },
};

/**
 * @returns the columns definition to be used in the search members table
 */
export function useSearchMembersTableColumns(): SearchMembersTableGridColDef[] {
  return useMemo(() => {
    const columnsMap: Record<
      SearchMembersTableColumnField,
      SearchMembersTableGridColDef
    > = {
      name: {
        field: "name",
        headerName: SEARCH_MEMBERS_TABLE_COLUMN_LABELS.name,
        ...columnDimension.name,
        valueGetter: (_, row) => getMemberDisplayName(row.entity),
        renderCell: MemberNameCell,
      },

      email: {
        field: "email",
        headerName: SEARCH_MEMBERS_TABLE_COLUMN_LABELS.email,
        ...columnDimension.email,
        valueGetter: (_, row) => row.entity.email,
        renderCell: MemberEmailCell,
      },

      workspace: {
        field: "workspace",
        headerName: SEARCH_MEMBERS_TABLE_COLUMN_LABELS.workspace,
        ...columnDimension.workspace,
        renderCell: MemberWorkspaceCell,
      },

      status: {
        field: "status",
        headerName: SEARCH_MEMBERS_TABLE_COLUMN_LABELS.status,
        ...columnDimension.status,
        valueGetter: (_, row) =>
          FaroCoreApiUtils.Member.isMemberActive(row.entity)
            ? UtilsTypes.MemberStatus.active
            : UtilsTypes.MemberStatus.pending,
        renderCell: (params) =>
          MemberStatusCell(params, "search-members-table"),
      },

      createdAt: {
        field: "createdAt",
        headerName: SEARCH_MEMBERS_TABLE_COLUMN_LABELS.createdAt,
        ...columnDimension.createdAt,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.createdAt,
        renderCell: MemberCreatedAtCell,
      },

      options: {
        field: "options",
        type: "actions",
        headerName: SEARCH_MEMBERS_TABLE_COLUMN_LABELS.options,
        ...columnDimension.options,
        getApplyQuickFilterFn: () => null,
        align: "right",
        renderCell: (params) =>
          MemberOptionCell({
            params,
            location: "search-members-table",
          }),
      },
    };
    return Object.values(columnsMap);
  }, []);
}
